import { assetTypeDefinition } from "../utils/projectAssets";
import React from "react";
import {
  Accordion,
  Alert,
  AccordionSummary,
  AccordionDetails,
  Box,
  Button,
  Divider,
  Grid,
  MenuItem,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { diagramSurfaceSx, getSchematicColors } from "../theme";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import LabeledSwitchWithHint from "./LabeledSwitchWithHint";

// Same helper as in ConfigForm/BESSForm — kept local so the panel is
// self-contained and reusable from any setup page.
function setPathInObject(obj, path, value) {
  const keys = path.split(".");
  const clone = { ...obj };
  let cur = clone;
  for (let i = 0; i < keys.length - 1; i++) {
    const k = keys[i];
    cur[k] = { ...(cur[k] ?? {}) };
    cur = cur[k];
  }
  cur[keys[keys.length - 1]] = value;
  return clone;
}

const isInvalidTransformerValue = (value) =>
  value === null ||
  value === undefined ||
  value === "" ||
  !Number.isFinite(Number(value)) ||
  Number(value) <= 0;

const labelSx = {
  color: "primary.main",
  fontWeight: 600,
  letterSpacing: "0.02em",
  "&.Mui-focused": { color: "primary.main" },
};

/** Full-width section header in the page flow (matches the accordion titles). */
function SectionHeader({ title, subtitle, first = false }) {
  return (
    <Box sx={{ mt: first ? 0 : 4, mb: 2 }}>
      <Typography
        variant="subtitle2"
        sx={{
          fontWeight: 600,
          textTransform: "uppercase",
          letterSpacing: 0.5,
          fontSize: "0.8rem",
          color: "text.primary",
        }}
      >
        {title}
      </Typography>
      {subtitle && (
        <Typography variant="caption" sx={{ color: "text.secondary" }}>
          {subtitle}
        </Typography>
      )}
      <Divider sx={{ mt: 1 }} />
    </Box>
  );
}

/* Mid-range of the bands in EU Ecodesign 548/2014, and the SAME constants the
   engine falls back to (Configuration/transformer_chain.py). Written into the
   fields by the button below rather than substituted behind the user's back, so
   what the panel shows is what the run uses. */
const ECODESIGN_NO_LOAD_PCT = 0.10;
const ECODESIGN_LOAD_PCT = 1.00;

/** One transformer's three datasheet fields (rated / no-load / load loss). */
function TransformerBlockCard({
  title,
  subtitle,
  dets,
  onField,          // (key, numericValue | "") => void
  onFields,         // ({key: value, ...}) => void — several fields in ONE commit
  requiredRated = false,
  ratedDisabled = false,
  ratedHelper,
  showErrors = false,
  headerSwitch = null,   // optional control (e.g. Recommended Rating) shown in the card
}) {
  const fields = [
    ["rated", "Apparent Rating Power (kVA)", ratedHelper || "Nameplate capacity"],
    ["noLoadLoss", "No-load Loss (%)",
      "Constant losses while energised — typical 0.05–0.15% of rated (EU Ecodesign 548/2014)"],
    ["loadLoss", "Load Loss (%)",
      "Load-dependent (copper) losses at nameplate — typical 0.7–1.2% of rated (EU Ecodesign 548/2014)"],
  ];
  const lossesAtStandard =
    Number(dets?.noLoadLoss) === ECODESIGN_NO_LOAD_PCT &&
    Number(dets?.loadLoss) === ECODESIGN_LOAD_PCT;
  return (
    <Paper variant="outlined" sx={{ p: 2, height: "100%" }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.25 }}>
        {title}
      </Typography>
      {subtitle && (
        <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mb: 1.5 }}>
          {subtitle}
        </Typography>
      )}
      {headerSwitch && <Box sx={{ mb: 1 }}>{headerSwitch}</Box>}
      <Grid container spacing={2}>
        {fields.map(([k, label, hint]) => {
          const missing =
            showErrors && requiredRated && k === "rated" && isInvalidTransformerValue(dets?.[k]);
          // With auto-sizing on, the stored rating is a leftover from whenever
          // it was last typed by hand: the engine ignores it and sizes the unit
          // itself at run time. Showing that stale number beneath the words
          // "Auto-sized" presents it as the answer, which it is not — on a
          // project that has grown since, it can be out by a factor of several.
          // Blank is the truthful display; the helper says what the size comes
          // from.
          const ratedAuto = k === "rated" && ratedDisabled;
          return (
            <Grid size={{ xs: 12, md: 4 }} key={k}>
              <TextField
                fullWidth
                size="small"
                label={label}
                type={ratedAuto ? "text" : "number"}
                value={ratedAuto ? "" : (dets?.[k] ?? "")}
                placeholder={ratedAuto ? "sized at run time" : undefined}
                disabled={ratedAuto}
                onChange={(e) =>
                  onField(k, e.target.value === "" ? "" : Number(e.target.value))
                }
                error={missing}
                helperText={missing ? "Required" : hint}
                InputLabelProps={{ sx: labelSx }}
              />
            </Grid>
          );
        })}
      </Grid>
      {/* An action, not a mode. Filling the cells makes the assumption visible
          and editable; a switch that quietly supplies the same numbers leaves
          the panel showing one thing and the run using another. */}
      <Box sx={{ mt: 1, display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
        <Button
          size="small"
          onClick={() => {
            // One commit for both cells. Two onField calls each rebuilt the
            // config from the same stale copy, so the second commit undid the
            // first and only the load loss ever changed.
            const patch = { noLoadLoss: ECODESIGN_NO_LOAD_PCT, loadLoss: ECODESIGN_LOAD_PCT };
            if (onFields) onFields(patch);
            else Object.entries(patch).forEach(([k, v]) => onField(k, v));
          }}
          sx={{ textTransform: "none" }}
        >
          Use standard values
        </Button>
        <Typography variant="caption" sx={{ color: "text.secondary" }}>
          {lossesAtStandard
            ? `At the EU Ecodesign 548/2014 mid-range (${ECODESIGN_NO_LOAD_PCT.toFixed(2)}% and ${ECODESIGN_LOAD_PCT.toFixed(2)}%) — replace with the datasheet figures.`
            : `Fills ${ECODESIGN_NO_LOAD_PCT.toFixed(2)}% and ${ECODESIGN_LOAD_PCT.toFixed(2)}%, the mid-range of EU Ecodesign 548/2014, for use until the datasheet arrives.`}
        </Typography>
      </Box>
    </Paper>
  );
}

/* ------------------------------------------------------------------ */
/* Indicative single-line view of the selected topology.               */
/*                                                                     */
/* Pure SVG, no data — it redraws from the same config the engine      */
/* reads, so the user SEES what "shared POI" vs "per asset block"      */
/* (and the optional common MV/HV) actually mean before running.       */
/* Solid double-circles = units the optimizer dispatches through;      */
/* dashed = units applied on a generation series before the solve.     */
/* ------------------------------------------------------------------ */
/* The diagram used to be drawn in fixed navy-on-slate, which stayed put
   when the rest of the UI went dark and left a white card in the middle
   of the panel. The palette now follows palette.mode. */
function useSchemaColors() {
  const { palette } = useTheme();
  return React.useMemo(() => getSchematicColors(palette.mode), [palette.mode]);
}

function TransformerGlyph({ x, y, dashed = false, label, sublabel }) {
  const c = useSchemaColors();
  const dash = dashed ? "4 3" : undefined;
  return (
    <g>
      <circle cx={x - 6} cy={y} r={12} fill="none" stroke={c.ink} strokeWidth="1.6" strokeDasharray={dash} />
      <circle cx={x + 6} cy={y} r={12} fill="none" stroke={c.ink} strokeWidth="1.6" strokeDasharray={dash} />
      {label && (
        <text x={x} y={y + 26} textAnchor="middle" fontSize="10" fill={c.muted}>
          {label}
        </text>
      )}
      {sublabel && (
        <text x={x} y={y + 37} textAnchor="middle" fontSize="9" fill={c.muted}>
          {sublabel}
        </text>
      )}
    </g>
  );
}

function TopologySchematic({ perAsset, includeHv, includeBess = true, hasPv, hasBess, hasWind, cableOn, chainActive }) {
  const c = useSchemaColors();
  const assets = [
    hasPv && { key: "pv", label: "PV", dashed: true, block: "PV block" },
    hasBess && {
      key: "bess", label: "BESS", dashed: false,
      block: includeBess ? "BESS block" : "direct (PCS output)",
      noGlyph: !includeBess,
    },
    hasWind && { key: "wind", label: "Wind", dashed: true, block: "Wind block" },
  ].filter(Boolean);
  if (!assets.length) return null;

  const rowGap = 62;
  const top = 30;
  const rows = assets.map((a, i) => ({ ...a, y: top + i * rowGap }));
  const midY = (rows[0].y + rows[rows.length - 1].y) / 2;
  const showDashedLegend = perAsset && rows.some((r) => r.dashed);
  const height = top + (rows.length - 1) * rowGap + 58 + (showDashedLegend ? 18 : 0);

  // x-coordinates of the stations along the flow
  const boxW = 86;
  const boxX = 8;
  const tPerAssetX = 176;   // per-asset transformer column
  const busX = perAsset ? 244 : 210;   // collection busbar
  const tSharedX = perAsset ? 330 : 292; // shared/common transformer column
  const poiX = 470;
  const gridX = 560;
  const hasMidUnit = perAsset ? includeHv : true; // shared_poi always has the shared unit
  const cableFrom = hasMidUnit ? tSharedX + 20 : busX;

  return (
    <svg
      viewBox={`0 0 660 ${height}`}
      style={{ width: "100%", maxWidth: 760, height: "auto", display: "block", margin: "0 auto" }}
      role="img"
      aria-label="Single-line view of the selected transformer topology"
    >
      {/* asset boxes and their feeders */}
      {rows.map((r) => (
        <g key={r.key}>
          <rect x={boxX} y={r.y - 16} width={boxW} height={32} rx={5}
                fill={c.assetFill} stroke={c.muted} strokeWidth="1.2" />
          <text x={boxX + boxW / 2} y={r.y + 4} textAnchor="middle" fontSize="11"
                fontWeight="600" fill={c.assetText}>{r.label}</text>
          {perAsset && r.noGlyph ? (
            <>
              <line x1={boxX + boxW} y1={r.y} x2={busX} y2={r.y}
                    stroke={c.ink} strokeWidth="1.4" />
              <text x={(boxX + boxW + busX) / 2} y={r.y + 14} textAnchor="middle"
                    fontSize="9" fill={c.muted}>
                {r.block}
              </text>
            </>
          ) : perAsset ? (
            <>
              <line x1={boxX + boxW} y1={r.y} x2={tPerAssetX - 20} y2={r.y}
                    stroke={c.ink} strokeWidth="1.4" />
              <TransformerGlyph x={tPerAssetX} y={r.y} dashed={r.dashed} label={r.block} />
              <line x1={tPerAssetX + 20} y1={r.y} x2={busX} y2={r.y}
                    stroke={c.ink} strokeWidth="1.4" />
            </>
          ) : (
            <line x1={boxX + boxW} y1={r.y} x2={busX} y2={r.y}
                  stroke={c.ink} strokeWidth="1.4" />
          )}
        </g>
      ))}

      {/* collection busbar (only meaningful with 2+ feeders) */}
      {rows.length > 1 && (
        <line x1={busX} y1={rows[0].y} x2={busX} y2={rows[rows.length - 1].y}
              stroke={c.ink} strokeWidth="2.4" />
      )}

      {/* shared / common unit and the run to the POI */}
      {hasMidUnit ? (
        <>
          <line x1={busX} y1={midY} x2={tSharedX - 20} y2={midY} stroke={c.ink} strokeWidth="1.4" />
          <TransformerGlyph
            x={tSharedX}
            y={midY}
            label={perAsset ? "Common MV/HV" : "Shared POI"}
            sublabel={perAsset ? "" : "transformer"}
          />
          <line x1={tSharedX + 20} y1={midY} x2={poiX - 6} y2={midY}
                stroke={c.ink} strokeWidth={cableOn ? 2.2 : 1.4} />
        </>
      ) : (
        <line x1={busX} y1={midY} x2={poiX - 6} y2={midY}
              stroke={c.ink} strokeWidth={cableOn ? 2.2 : 1.4} />
      )}
      {cableOn && (
        <text x={(cableFrom + poiX) / 2} y={midY - 8} textAnchor="middle" fontSize="9.5" fill={c.muted}>
          AC cable run (I²R)
        </text>
      )}

      {/* POI node and the grid */}
      <circle cx={poiX} cy={midY} r={5} fill={c.ink} />
      <text x={poiX} y={midY - 12} textAnchor="middle" fontSize="10" fontWeight="600" fill={c.ink}>
        POI
      </text>
      <line x1={poiX + 5} y1={midY} x2={gridX - 4} y2={midY} stroke={c.ink} strokeWidth="1.4" />
      <rect x={gridX} y={midY - 16} width={72} height={32} rx={5}
            fill={c.gridFill} stroke={c.ink} strokeWidth="1.4" />
      <text x={gridX + 36} y={midY + 4} textAnchor="middle" fontSize="11" fontWeight="600" fill={c.ink}>
        Grid
      </text>

      {/* legend */}
      <g transform={`translate(8, ${height - (showDashedLegend ? 26 : 14)})`}>
        <circle cx={6} cy={0} r={6} fill="none" stroke={c.ink} strokeWidth="1.4" />
        <circle cx={12} cy={0} r={6} fill="none" stroke={c.ink} strokeWidth="1.4" />
        <text x={26} y={3.5} fontSize="9.5" fill={c.muted}>
          {chainActive
            ? "solid — dispatched through the optimization"
            : "solid — dispatched through the optimization (pick a Loss Modelling mode above to activate)"}
        </text>
      </g>
      {showDashedLegend && (
        <g transform={`translate(8, ${height - 8})`}>
          <circle cx={6} cy={0} r={6} fill="none" stroke={c.ink} strokeWidth="1.4" strokeDasharray="3 2.5" />
          <circle cx={12} cy={0} r={6} fill="none" stroke={c.ink} strokeWidth="1.4" strokeDasharray="3 2.5" />
          <text x={26} y={3.5} fontSize="9.5" fill={c.muted}>
            dashed — applied directly on the generation profile (PV / wind)
          </text>
        </g>
      )}
    </svg>
  );
}

/**
 * Transformer & POI-chain settings (dev spec section 4 / A3).
 *
 * Page flow (top to bottom):
 *   1. Include Transformer / topology / recommended rating
 *   2. TRANSFORMER UNITS   — one card per unit, per the selected topology
 *   3. LOSS MODELLING IN THE DISPATCH — chain mode and its numeric settings
 *   4. CABLE LOSSES        — optional I²R run folded into the same chain
 *
 * Topologies:
 *  - shared_poi: ONE common transformer at the POI for every asset.
 *  - per_asset: one transformer per asset block (+ optional common MV/HV).
 *    PV/wind-block units apply exogenously on their generation series; the
 *    battery-block and common units enter the MILP.
 *
 * Config layout (cfg.transformerDets): rated/noLoadLoss/loadLoss = the shared
 * POI unit (shared_poi) or the PV-block unit (per_asset); battery{...},
 * wind{...}, hv{...} = the per-asset blocks; cable{...} = the AC run.
 */
export default function TransformerPanel({ cfg, onCommit, showTransformerErrors = false }) {
  const transformerDets = cfg.transformerDets || {};
  const chainLossMode = transformerDets.chainLossMode ?? "off";
  const transformerTopology = transformerDets.topology ?? "shared_poi";

  // Which asset blocks exist in this project (drives the per_asset cards).
  const type = assetTypeDefinition(cfg.systemType);
  const hasPvAsset = Boolean(type?.hasPv);
  const hasWindAsset = Boolean(type?.hasWind);
  const hasBessAsset = Boolean(type?.hasBess);

  const handleField = (path, value) => onCommit(setPathInObject(cfg, path, value));

  // A plant that exports to MV or HV has a step-up transformer, whatever this
  // switch says. The default project declares an MV connection and ships this
  // switch off, so the common case is a plant simulated exporting straight from
  // inverter low voltage -- about 1% of annual production missing, and a line
  // missing from the loss diagram a reviewer will compare against PVsyst.
  // Stated rather than forced: a deliberate choice stays the user's.
  const connectionLevel = String(cfg?.grid?.connectionLevel || "").toUpperCase();
  const transformerConnected = connectionLevel === "MV" || connectionLevel === "HV";
  const showMissingTransformer = transformerConnected && !cfg.hasTransformer;

  const enableTransformer = () =>
    onCommit(
      setPathInObject(
        setPathInObject(cfg, "hasTransformer", true),
        "useRecommendedTransformer",
        true
      )
    );
  const handleTransformerField = (key, rawValue) =>
    handleField(`transformerDets.${key}`, Number(rawValue));
  const handleTransformerFieldRaw = (key, value) =>
    handleField(`transformerDets.${key}`, value);
  // Several transformer fields in one commit (prefix = "" | "battery." | "hv.").
  const handleTransformerFields = (prefix, patch) =>
    onCommit(
      Object.entries(patch).reduce(
        (acc, [k, v]) => setPathInObject(acc, `transformerDets.${prefix}${k}`, v),
        cfg
      )
    );

  const perAsset = transformerTopology === "per_asset";
  // Explicit opt-out: a BESS block without a dedicated transformer (e.g. an
  // MV PCS with integrated magnetics). Default true = today's behaviour.
  const includeBessUnit = transformerDets.includeBessUnit ?? true;
  const chainActive = chainLossMode !== "off";
  const cableOn = Boolean(transformerDets.includeCableLosses);

  return (
    <Accordion defaultExpanded>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography
          variant="subtitle2"
          sx={{
            fontWeight: 600,
            mb: 1,
            textTransform: "uppercase",
            letterSpacing: 0.5,
            fontSize: "0.85rem",
            color: "text.primary",
          }}
        >
          Transformers
        </Typography>
      </AccordionSummary>

      <AccordionDetails sx={{ display: "block" }}>
        {showMissingTransformer && (
          <Alert
            severity="warning"
            sx={{ mb: 2 }}
            action={
              <Button color="inherit" size="small" onClick={enableTransformer}>
                ENABLE
              </Button>
            }
          >
            This project connects at <b>{connectionLevel}</b>, so it exports through a
            step-up transformer — but none is modelled. Its no-load and load losses,
            typically around 1% of annual production, are missing from the yield and
            from the loss diagram. Enable it, or state the omission in the report.
          </Alert>
        )}
        {/* -------- 1. Selection row: toggle and topology side by side -------- */}
        <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 2.5, width: "100%" }}>
          <Box id="field-grid-transformer">
            <LabeledSwitchWithHint
              label="Include Transformer"
              checked={Boolean(cfg.hasTransformer)}
              onChange={(e) => handleField("hasTransformer", e.target.checked)}
              hint="Enable if the installation connects through one or more step-up transformers (e.g., low-voltage to medium-voltage). Required when exporting to an MV grid."
            />
          </Box>
          {cfg.hasTransformer && (
            <>
              <TextField
                select
                size="small"
                label="Topology"
                value={transformerTopology}
                onChange={(e) => handleTransformerFieldRaw("topology", e.target.value)}
                helperText="How the assets share the transformer(s)"
                sx={{ minWidth: 300 }}
              >
                <MenuItem value="shared_poi">One shared transformer at the POI</MenuItem>
                <MenuItem value="per_asset">One per asset block</MenuItem>
              </TextField>
              <TextField
                select
                size="small"
                label="Loss Modelling"
                value={chainLossMode}
                onChange={(e) => handleTransformerFieldRaw("chainLossMode", e.target.value)}
                helperText={
                  chainLossMode === "off"
                    ? "Transformer losses apply to the PV output only — the battery flow carries none"
                    : chainLossMode === "opex_only"
                    ? "The dispatch is unchanged; the energy lost in the chain is priced and booked as an operating cost"
                    : "The loss curves are embedded in the optimization — the dispatch adapts to them"
                }
                sx={{
                  minWidth: 340,
                  ...(chainLossMode === "off"
                    ? { "& .MuiFormHelperText-root": { color: "warning.main" },
                        "& .MuiOutlinedInput-notchedOutline": { borderColor: "warning.main" },
                        "& .MuiInputLabel-root": { color: "warning.main" } }
                    : {}),
                }}
              >
                <MenuItem value="off">Not modelled — PV-side losses only</MenuItem>
                <MenuItem value="opex_only">Economics only — costed after dispatch</MenuItem>
                <MenuItem value="in_dispatch">Fully integrated — losses in the dispatch</MenuItem>
              </TextField>
            </>
          )}
        </Box>

        {/* -------- 1b. What the selection means, drawn -------- */}
        {cfg.hasTransformer && (
          <Paper variant="outlined" sx={{ mt: 2, p: 1.5, width: "100%", ...diagramSurfaceSx }}>
            <Typography
              variant="caption"
              sx={{
                color: "text.secondary",
                fontWeight: 600,
                textTransform: "uppercase",
                letterSpacing: 0.5,
                display: "block",
                mb: 1,
              }}
            >
              Selected topology — indicative single-line view
            </Typography>
            <TopologySchematic
              perAsset={perAsset}
              includeHv={Boolean(transformerDets.includeHv)}
              includeBess={includeBessUnit}
              hasPv={hasPvAsset}
              hasBess={hasBessAsset}
              hasWind={hasWindAsset}
              cableOn={cableOn}
              chainActive={chainActive}
            />
          </Paper>
        )}

        {cfg.hasTransformer && transformerTopology !== "none" && (
          <>
            {/* -------- 2. Transformer units -------- */}
            <SectionHeader
              title="Transformer Units"
              subtitle={
                perAsset
                  ? "One unit per asset block. PV/wind units are applied directly on their generation profiles; the BESS-block and common MV/HV units are modelled inside the optimization."
                  : "One common unit at the POI, crossed by every asset's flow."
              }
            />
            <Grid container spacing={2}>
              {!perAsset && (
                <Grid size={12}>
                  <TransformerBlockCard
                    title="Shared POI transformer"
                    subtitle="Common unit for every asset (PV, battery, wind)."
                    dets={transformerDets}
                    onField={(k, v) => handleTransformerFieldRaw(k, v)}
                        onFields={(patch) => handleTransformerFields("", patch)}
                    requiredRated={!cfg.useRecommendedTransformer}
                    ratedDisabled={Boolean(cfg.useRecommendedTransformer)}
                    ratedHelper={
                      cfg.useRecommendedTransformer
                        ? "Sized at run time: inverter capacity plus battery discharge power over the power factor, never below the contracted connection limit."
                        : "Nameplate capacity"
                    }
                    showErrors={showTransformerErrors}
                    headerSwitch={(() => {
                      const on = Boolean(cfg.useRecommendedTransformer);
                      return (
                        <LabeledSwitchWithHint
                          label="Recommended Rating"
                          checked={on}
                          onChange={(e) => {
                            const next0 = e.target.checked;
                            let next = setPathInObject(cfg, "useRecommendedTransformer", next0);
                            if (next0) {
                              const cur = (next.transformerDets || {}).rated;
                              if (cur == null || String(cur).trim() === "") {
                                next = setPathInObject(next, "transformerDets.rated", 0);
                              }
                            }
                            onCommit(next);
                          }}
                          hint={"Auto-sizes this unit: inverter capacity plus battery discharge power (over the power factor), and never below a real POI limit."}
                        />
                      );
                    })()}
                  />
                </Grid>
              )}
              {perAsset && (
                <>
                  {hasPvAsset && (
                    <Grid size={12}>
                      <TransformerBlockCard
                        title="PV block"
                        subtitle="Applied directly on the PV generation profile (its flow is known in advance)."
                        dets={transformerDets}
                        onField={(k, v) => handleTransformerFieldRaw(k, v)}
                        onFields={(patch) => handleTransformerFields("", patch)}
                        requiredRated={!cfg.useRecommendedTransformer}
                        ratedDisabled={Boolean(cfg.useRecommendedTransformer)}
                        ratedHelper={
                          cfg.useRecommendedTransformer
                            ? "Sized at run time: inverter capacity plus battery discharge power over the power factor, never below the contracted connection limit."
                            : "Nameplate capacity"
                        }
                        showErrors={showTransformerErrors}
                        headerSwitch={(() => {
                      const on = Boolean(cfg.useRecommendedTransformer);
                      return (
                        <LabeledSwitchWithHint
                          label="Recommended Rating"
                          checked={on}
                          onChange={(e) => {
                            const next0 = e.target.checked;
                            let next = setPathInObject(cfg, "useRecommendedTransformer", next0);
                            if (next0) {
                              const cur = (next.transformerDets || {}).rated;
                              if (cur == null || String(cur).trim() === "") {
                                next = setPathInObject(next, "transformerDets.rated", 0);
                              }
                            }
                            onCommit(next);
                          }}
                          hint={"Auto-sizes the PV-block unit from the inverter capacity (it carries PV flow alone). The BESS-block and MV/HV units are always sized manually."}
                        />
                      );
                    })()}
                      />
                    </Grid>
                  )}
                  {hasBessAsset && (
                    <Grid size={12}>
                      {includeBessUnit ? (
                        <TransformerBlockCard
                          title="BESS block"
                          subtitle="Enters the optimization on the battery flow (charge and discharge)."
                          dets={transformerDets.battery || {}}
                          onField={(k, v) => handleTransformerFieldRaw(`battery.${k}`, v)}
                        onFields={(patch) => handleTransformerFields("battery.", patch)}
                          requiredRated={chainActive}
                          showErrors={chainActive}
                          headerSwitch={
                            <LabeledSwitchWithHint
                              label="Dedicated BESS-block transformer"
                              checked
                              onChange={() => handleTransformerFieldRaw("includeBessUnit", false)}
                              hint="Turn off when the battery has no dedicated step-up unit as separate equipment — e.g. an MV PCS skid with integrated transformer (fit the PCS k0/k1 from the skid's COMBINED efficiency curve so its magnetics stay accounted for), or a small LV-connected system. A battery tied behind the PV transformer is better modelled as shared_poi. Declared in the report assumptions."
                            />
                          }
                        />
                      ) : (
                        <Paper variant="outlined" sx={{ p: 2 }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                            BESS block
                          </Typography>
                          <LabeledSwitchWithHint
                            label="Dedicated BESS-block transformer"
                            checked={false}
                            onChange={() => handleTransformerFieldRaw("includeBessUnit", true)}
                            hint="Turn on to model a dedicated step-up unit on the battery flow."
                          />
                          <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mt: 0.5 }}>
                            No dedicated unit — the battery connects at the collection bus at
                            the PCS output (e.g. an MV PCS skid with integrated transformer).
                            Only the common MV/HV unit (if included) and the cable run apply
                            on the battery flow. If the PCS skid integrates the magnetics, fit
                            the PCS losses (Battery Performance) from the skid&apos;s combined
                            efficiency curve so they stay accounted for. Declared explicitly in
                            the report assumptions.
                          </Typography>
                        </Paper>
                      )}
                    </Grid>
                  )}
                  {hasWindAsset && (
                    <Grid size={12}>
                      <TransformerBlockCard
                        title="Wind block"
                        subtitle="Applied on the wind generation series (exogenous). Carried in the study configuration; becomes active in dispatch once wind enters the feasibility optimization."
                        dets={transformerDets.wind || {}}
                        onField={(k, v) => handleTransformerFieldRaw(`wind.${k}`, v)}
                      />
                    </Grid>
                  )}
                  <Grid size={12}>
                    {Boolean(transformerDets.includeHv) ? (
                      <TransformerBlockCard
                        title="Common MV/HV"
                        subtitle="Grid-side unit above the asset blocks, shared by the whole station."
                        dets={transformerDets.hv || {}}
                        onField={(k, v) => handleTransformerFieldRaw(`hv.${k}`, v)}
                        onFields={(patch) => handleTransformerFields("hv.", patch)}
                        requiredRated={chainActive}
                        showErrors={chainActive}
                        headerSwitch={
                          <LabeledSwitchWithHint
                            label="Include common MV/HV unit"
                            checked
                            onChange={() => handleTransformerFieldRaw("includeHv", false)}
                            hint="A shared grid-side transformer above the asset blocks. Enters the optimization on the total POI flow."
                          />
                        }
                      />
                    ) : (
                      <Paper variant="outlined" sx={{ p: 2 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                          Common MV/HV
                        </Typography>
                        <LabeledSwitchWithHint
                          label="Include common MV/HV unit"
                          checked={false}
                          onChange={() => handleTransformerFieldRaw("includeHv", true)}
                          hint="A shared grid-side transformer above the asset blocks (e.g. the MV/HV step-up of the whole station). Enters the optimization on the total POI flow."
                        />
                      </Paper>
                    )}
                  </Grid>
                </>
              )}
            </Grid>

            {/* -------- 3. Loss modelling — advanced settings (mode lives
                 beside the topology above; these refine it) -------- */}
            {chainActive && (
            <>
            <SectionHeader
              title="Loss Modelling"
              subtitle="A transformer loses power two ways: the copper loss follows the flow through it and rises with the square of loading, while the iron loss follows the clock and is paid for as long as the unit is energised. These two settings fix the flow the first one sees and the hours the second one is charged for."
            />
            <Grid container spacing={3}>
              {chainActive && (
                <>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                      fullWidth
                      label="Power Factor"
                      type="number"
                      value={transformerDets.powerFactor ?? 0.95}
                      onChange={(e) => handleTransformerField("powerFactor", e.target.value)}
                      inputProps={{ min: 0.8, max: 1.0, step: 0.01 }}
                      helperText={`${
                        (1 / (Number(transformerDets.powerFactor ?? 0.95) || 0.95)).toFixed(2)
                      } MVA of loading for every 1 MW passing through.`}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                      fullWidth
                      label="Energised Hours / Year"
                      type="number"
                      value={transformerDets.energisedHoursYear ?? 8760}
                      onChange={(e) => handleTransformerField("energisedHoursYear", e.target.value)}
                      inputProps={{ min: 0, max: 8784, step: 1 }}
                      helperText={
                        Number(transformerDets.energisedHoursYear ?? 8760) >= 8760
                          ? "Never de-energised — the no-load loss is paid every hour of the year."
                          : "The no-load loss is paid for these hours only; disconnected for the rest."
                      }
                    />
                  </Grid>
                  {/* The "Linearization Segments" field stood here. It set how
                      many straight chords approximate the quadratic load-loss
                      curve — a numerical-method question, not a project one:
                      the same answer is right for every plant, and nothing on
                      site or in a datasheet tells a user to pick differently.
                      The error falls as 1/N^2 and the solve time rises roughly
                      linearly, so the engine is fixed at N = 6 (see
                      Configuration/transformer_chain.LOSS_CURVE_SEGMENTS for the
                      measurement): worst-case over-estimate ~2.5 % of the load
                      loss, an order of magnitude inside the +15 % IEC 60076-1
                      already allows the measured Pk itself. The value the model
                      uses is now disclosed in the report's assumptions, where a
                      method assumption belongs. Stored projects keep the old key
                      harmlessly — the engine reads it only to log that it no
                      longer applies. */}
                  {/* The Negative-Price Handling selector stood here. Its
                      "relaxed" option let the optimizer inflate the loss in
                      hours it is paid to import, and it only ever bought the
                      segment binaries of those hours back. That trade was
                      priced on an assumption the market has outgrown — a
                      handful of negative hours a year — and even at the limit,
                      a day negative in every hour, it saves 96 binaries out of
                      216. The engine now pins the loss in every hour and the
                      choice is gone rather than merely discouraged. */}
                  {/* The "Ecodesign Defaults" switch that stood here did
                      nothing in any position. The engine parses the flag and
                      stores it, but the substitution in Opt_feasibility/
                      bess_rules.py is a plain `or` on each loss field: a blank
                      cell takes the standard value whether the switch is on or
                      off, and a filled cell keeps its own. Replaced by the
                      "Use standard values" button on each transformer card,
                      which writes the same two figures into the fields where
                      they can be seen, edited, and reported for what they are. */}
                </>
              )}
            </Grid>
            </>
            )}

            {/* -------- 4. Cable losses -------- */}
            {chainActive && (
              <>
                <SectionHeader
                  title="Cable Losses"
                  subtitle="The interconnection run between the plant substation and the grid's point of connection — metering sits at the POI, so its I²R losses are borne by the producer. Model it when the step-up transformer is at the plant and the POI is a separate location (a typical MV interconnection line); leave it OFF when the transformer sits inside the POI substation itself and the run is a few metres of busbar. New projects start with typical values (150 m, 240 mm² Cu, single run) — review them for the site."
                />
                <Grid container spacing={3} alignItems="center">
                  <Grid size={{ xs: 12, md: 4 }}>
                    <LabeledSwitchWithHint
                      label="Include Cable Losses"
                      checked={cableOn}
                      onChange={(e) => {
                        const on = e.target.checked;
                        let next = setPathInObject(cfg, "transformerDets.includeCableLosses", on);
                        if (on) {
                          // Seed the typical MV feeder values when the geometry
                          // is empty, so enabling never lands on red "Required".
                          const c = next.transformerDets?.cable || {};
                          if (!(Number(c.lengthM) > 0)) {
                            next = setPathInObject(next, "transformerDets.cable.lengthM", 150);
                          }
                          if (!(Number(c.sectionMm2) > 0)) {
                            next = setPathInObject(next, "transformerDets.cable.sectionMm2", 240);
                          }
                          if (!c.material) {
                            next = setPathInObject(next, "transformerDets.cable.material", "Cu");
                          }
                          if (!(Number(c.parallelRuns) >= 1)) {
                            next = setPathInObject(next, "transformerDets.cable.parallelRuns", 1);
                          }
                        }
                        onCommit(next);
                      }}
                      hint="Fold the I²R losses of the AC run to the POI into the same loss chain. Enabling pre-fills typical MV feeder values — adjust them to the site."
                    />
                  </Grid>

                  {cableOn && (
                    <>
                      <Grid size={{ xs: 12, md: 4 }}>
                        <TextField
                          fullWidth
                          label="Cable Length (m)"
                          type="number"
                          value={transformerDets.cable?.lengthM ?? ""}
                          onChange={(e) =>
                            handleTransformerFieldRaw(
                              "cable.lengthM",
                              e.target.value === "" ? "" : Number(e.target.value)
                            )
                          }
                          inputProps={{ min: 0, step: 1 }}
                          error={!(Number(transformerDets.cable?.lengthM) > 0)}
                          helperText={
                            !(Number(transformerDets.cable?.lengthM) > 0)
                              ? "Required to derive the cable resistance"
                              : "One-way route length of the AC run"
                          }
                        />
                      </Grid>
                      <Grid size={{ xs: 12, md: 4 }}>
                        <TextField
                          fullWidth
                          label="Cable Section (mm²)"
                          type="number"
                          value={transformerDets.cable?.sectionMm2 ?? ""}
                          onChange={(e) =>
                            handleTransformerFieldRaw(
                              "cable.sectionMm2",
                              e.target.value === "" ? "" : Number(e.target.value)
                            )
                          }
                          inputProps={{ min: 0, step: 5 }}
                          error={!(Number(transformerDets.cable?.sectionMm2) > 0)}
                          helperText={
                            !(Number(transformerDets.cable?.sectionMm2) > 0)
                              ? "Required to derive the cable resistance"
                              : "Conductor cross-section per run"
                          }
                        />
                      </Grid>
                      <Grid size={{ xs: 12, md: 4 }}>
                        <TextField
                          fullWidth
                          select
                          label="Conductor Material"
                          value={transformerDets.cable?.material ?? "Cu"}
                          onChange={(e) =>
                            handleTransformerFieldRaw("cable.material", e.target.value)
                          }
                          helperText="Copper or aluminium (AC resistivity at 60°C)"
                        >
                          <MenuItem value="Cu">Copper (Cu)</MenuItem>
                          <MenuItem value="Al">Aluminium (Al)</MenuItem>
                        </TextField>
                      </Grid>
                      <Grid size={{ xs: 12, md: 4 }}>
                        <TextField
                          fullWidth
                          label="Parallel Runs"
                          type="number"
                          value={transformerDets.cable?.parallelRuns ?? 1}
                          onChange={(e) =>
                            handleTransformerFieldRaw(
                              "cable.parallelRuns",
                              e.target.value === "" ? "" : Number(e.target.value)
                            )
                          }
                          inputProps={{ min: 1, max: 12, step: 1 }}
                          helperText="Cables per phase in parallel"
                        />
                      </Grid>
                      <Grid size={{ xs: 12, md: 4 }}>
                        <LabeledSwitchWithHint
                          label="Run at grid connection voltage"
                          checked={!(Number(transformerDets.cable?.acVoltageV) > 0)}
                          onChange={(e) =>
                            handleTransformerFieldRaw(
                              "cable.acVoltageV",
                              e.target.checked ? "" : Number(cfg.grid?.voltageV ?? 400)
                            )
                          }
                          hint="On (default): the run sits on the grid side of the transformer and operates at the connection voltage from the Grid Connection settings — losses scale with 1/V². Turn off only for a run at a different voltage, e.g. a PCS-side feeder at 690 V."
                        />
                        <Typography
                          variant="caption"
                          sx={{ color: "text.secondary", display: "block", ml: 0.5, mt: -0.5 }}
                        >
                          {!(Number(transformerDets.cable?.acVoltageV) > 0)
                            ? `Using the connection voltage${cfg.grid?.voltageV ? ` (${Number(cfg.grid.voltageV).toLocaleString("en-US")} V)` : ""}.`
                            : "Custom run voltage:"}
                        </Typography>
                        {Number(transformerDets.cable?.acVoltageV) > 0 && (
                          <TextField
                            fullWidth
                            size="small"
                            label="Run Voltage (V, line-to-line)"
                            type="number"
                            value={transformerDets.cable?.acVoltageV ?? ""}
                            onChange={(e) =>
                              handleTransformerFieldRaw(
                                "cable.acVoltageV",
                                e.target.value === "" ? "" : Number(e.target.value)
                              )
                            }
                            inputProps={{ min: 1, step: 10 }}
                            sx={{ mt: 0.5 }}
                            helperText="Operating voltage of this specific run (e.g. 690 V PCS-side)"
                          />
                        )}
                      </Grid>
                    </>
                  )}
                </Grid>
              </>
            )}
          </>
        )}
      </AccordionDetails>
    </Accordion>
  );
}
